/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { css, jsx } from '@compiled/react';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';

const styles = cssMap({
	frame: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '48px',
		height: '48px',
	},
	avatar: {
		flexShrink: 0,
		width: '96px',
		height: '96px',
		transform: 'scale(0.5625)',
		transformOrigin: 'center',
	},
	hexagon: {
		// Match the rounded hexagon used by @atlaskit/avatar.
		clipPath:
			'polygon(45% 1.33975%, 46.5798% 0.60307%, 48.26352% 0.15192%, 50% 0%, 51.73648% 0.15192%, 53.4202% 0.60307%, 55% 1.33975%, 89.64102% 21.33975%, 91.06889% 22.33956%, 92.30146% 23.57212%, 93.30127% 25%, 94.03794% 26.5798%, 94.48909% 28.26352%, 94.64102% 30%, 94.64102% 70%, 94.48909% 71.73648%, 94.03794% 73.4202%, 93.30127% 75%, 92.30146% 76.42788%, 91.06889% 77.66044%, 89.64102% 78.66025%, 55% 98.66025%, 53.4202% 99.39693%, 51.73648% 99.84808%, 50% 100%, 48.26352% 99.84808%, 46.5798% 99.39693%, 45% 98.66025%, 10.35898% 78.66025%, 8.93111% 77.66044%, 7.69854% 76.42788%, 6.69873% 75%, 5.96206% 73.4202%, 5.51091% 71.73648%, 5.35898% 70%, 5.35898% 30%, 5.51091% 28.26352%, 5.96206% 26.5798%, 6.69873% 25%, 7.69854% 23.57212%, 8.93111% 22.33956%, 10.35898% 21.33975%)',
	},
});

// Compiled supports the child selector needed to size consumer-provided images.
const imageStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- The frame owns sizing for direct img children, including ADS Image.
	'& > img': {
		display: 'block',
		width: '100%',
		height: '100%',
		objectFit: 'cover',
		objectPosition: 'center',
	},
});

export type CardAvatarFrameProps = {
	/** Non-interactive content. Direct img children (including ADS Image) are sized automatically. */
	children?: React.ReactNode;
	/** Accessible name for the fallback avatar. Omit when the avatar is decorative. */
	name?: string;
	/** Crop the content to the rounded ADS hexagon. Defaults to false. */
	isHexagon?: boolean;
};

/** Size and center avatar content, or an ADS fallback, within the card's layout slot. */
export const CardAvatarFrame: (props: CardAvatarFrameProps) => React.JSX.Element = ({
	children,
	name,
	isHexagon = false,
}) => (
	<Box xcss={styles.frame}>
		<div css={[styles.avatar, imageStyles, isHexagon && styles.hexagon]}>
			{children ?? (
				<Avatar name={name} appearance={isHexagon ? 'hexagon' : 'circle'} size="xlarge" />
			)}
		</div>
	</Box>
);
